import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const W = 1080;
const H = 1080;

function GraphicRecap({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam, homeScore, awayTeam, awayScore,
    gameDate, topPerformer, topPerformerStats, backgroundPhoto,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);
  const resultLabel = isTie ? 'TIE GAME' : homeWon ? 'VICTORY' : 'FINAL SCORE';

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#ffffff';

  const getBackground = () => {
    const p = teamColors.primary;
    const s = teamColors.secondary;
    switch (backgroundStyle) {
      case 'radial-gradient': return `radial-gradient(circle at center, ${p} 0%, ${s} 100%)`;
      case 'diagonal-gradient': return `linear-gradient(45deg, ${p} 0%, ${s} 100%)`;
      case 'split-two-tone': return `linear-gradient(90deg, ${p} 0%, ${p} 50%, ${s} 50%, ${s} 100%)`;
      case 'solid-primary': return p;
      case 'solid-secondary': return s;
      default: return `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;
    }
  };

  const badgeFontSize = Math.max(32, Math.min(Math.round(heroFontSize / 2.5), 96));
  const cardPadding = heroFontSize > 240 ? '20px 16px' : heroFontSize > 180 ? '28px 20px' : '40px 24px';
  const logoCircleSize = heroFontSize > 240 ? 80 : heroFontSize > 180 ? 100 : 120;

  const homeNameFontSize = (homeTeam || 'YOUR TEAM').length > 14 ? 52 : (homeTeam || 'YOUR TEAM').length > 9 ? 64 : 80;
  const awayNameFontSize = (awayTeam || 'OPPONENT').length > 14 ? 52 : (awayTeam || 'OPPONENT').length > 9 ? 64 : 80;
  const scoreFontSize = 160;

  const logoCircleStyle = {
    width: logoCircleSize,
    height: logoCircleSize,
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    marginBottom: 20,
  };

  const winnerStyle = {
    transform: 'scale(1.05)',
  };

  return (
    <div
      id="graphic-recap"
      style={{
        width: W,
        height: H,
        position: 'relative',
        overflow: 'hidden',
        background: getBackground(),
        borderRadius: 16,
        fontFamily: 'Barlow, sans-serif',
        border: '2px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Background Photo */}
      {backgroundPhoto && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <img
            src={backgroundPhoto}
            alt="Background"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)',
          }} />
        </div>
      )}

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '48px 80px 40px',
        }}
      >
        {/* Result Badge */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              display: 'inline-block',
              background: '#FFFFFF',
              color: accent,
              fontFamily: heroFont,
              fontSize: badgeFontSize,
              fontWeight: 800,
              letterSpacing: 4,
              padding: '16px 56px',
              borderRadius: 60,
              border: `3px solid ${accent}`,
            }}
          >
            {resultLabel}
          </div>
        </div>

        {/* Scoreboard */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 32,
            flex: 1,
            minHeight: 0,
            marginBottom: 24,
          }}
        >
          {/* Home Team */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: cardPadding,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 24,
              border: `2px solid ${homeWon ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)'}`,
              ...(homeWon ? winnerStyle : {}),
            }}
          >
            <div style={logoCircleStyle}>
              {homeTeamLogo ? (
                <img
                  src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                  alt={homeTeam}
                  style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
                />
              ) : (
                <span style={{ fontSize: 60 }}>
                  {teamSport === 'Softball' ? '🥎' : '⚾'}
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: homeNameFontSize,
                fontWeight: 800,
                color: '#ffffff',
                textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                textTransform: 'uppercase',
                letterSpacing: 2,
                textAlign: 'center',
                marginBottom: 16,
                lineHeight: 1,
              }}
            >
              {homeTeam || 'YOUR TEAM'}
            </div>
            <div
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: scoreFontSize,
                fontWeight: 900,
                color: '#ffffff',
                lineHeight: 1,
                textShadow: '0 4px 16px rgba(0,0,0,0.6)',
              }}
            >
              {homeScore || '0'}
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 80,
              fontWeight: 900,
              color: 'rgba(255,255,255,0.6)',
              flexShrink: 0,
            }}
          >
            -
          </div>

          {/* Away Team */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: cardPadding,
              background: 'rgba(255,255,255,0.12)',
              borderRadius: 24,
              border: `2px solid ${!homeWon && !isTie ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)'}`,
              ...(!homeWon && !isTie ? winnerStyle : {}),
            }}
          >
            <div style={logoCircleStyle}>
              {awayTeamLogoPreview ? (
                <img
                  src={awayTeamLogoPreview}
                  alt={awayTeam}
                  style={{ width: 'auto', height: 'auto', maxWidth: '80%', maxHeight: '80%' }}
                />
              ) : (
                <span style={{ fontSize: 60 }}>
                  {teamSport === 'Softball' ? '🥎' : '⚾'}
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: awayNameFontSize,
                fontWeight: 800,
                color: '#ffffff',
                textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                textTransform: 'uppercase',
                letterSpacing: 2,
                textAlign: 'center',
                marginBottom: 16,
                lineHeight: 1,
              }}
            >
              {awayTeam || 'OPPONENT'}
            </div>
            <div
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: scoreFontSize,
                fontWeight: 900,
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1,
                textShadow: '0 4px 16px rgba(0,0,0,0.6)',
              }}
            >
              {awayScore || '0'}
            </div>
          </div>
        </div>

        {/* Player of the Game */}
        {topPerformer && (
          <div
            style={{
              background: 'rgba(0,0,0,0.4)',
              border: `2px solid ${accent}`,
              borderRadius: 20,
              padding: '28px 40px',
              textAlign: 'center',
              marginBottom: 32,
            }}
          >
            <div
              style={{
                fontFamily: 'Barlow, sans-serif',
                fontSize: 24,
                fontWeight: 700,
                color: accent,
                letterSpacing: 4,
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              ⭐ PLAYER OF THE GAME
            </div>
            <div
              style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: 72,
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: 2,
                lineHeight: 1,
                marginBottom: 8,
              }}
            >
              {topPerformer}
            </div>
            {topPerformerStats && (
              <div
                style={{
                  fontFamily: 'Barlow, sans-serif',
                  fontSize: 32,
                  fontWeight: 600,
                  color: detailColor,
                  letterSpacing: 1,
                }}
              >
                {topPerformerStats}
              </div>
            )}
          </div>
        )}

        {/* Date */}
        {gameDate && (
          <div
            style={{
              textAlign: 'center',
              fontFamily: 'Barlow, sans-serif',
              fontSize: 28,
              fontWeight: 700,
              color: detailColor,
              letterSpacing: 3,
              textTransform: 'uppercase',
              opacity: 0.9,
            }}
          >
            {formatGameDate(gameDate, dateFormat)}
          </div>
        )}
      </div>

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicRecap;
